import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import JudiMark from "../ui/JudiMark";
import { NAV_LINKS } from "./navLinks";

interface LandingHeaderProps {
  onSignIn: () => void;
  isLoading: boolean;
}

/**
 * Sticky top bar. Transparent over the hero, then picks up a blurred
 * background and a border once the page has scrolled past it.
 */
export default function LandingHeader({
  onSignIn,
  isLoading,
}: LandingHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-warm-bg-base/80 backdrop-blur-xl shadow-soft-sm border-b border-warm-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-16">
        <div className="flex items-center gap-2.5">
          <JudiMark className="w-7 h-7" />
          <span className="font-display text-2xl text-warm-text-primary italic">
            JuDI
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-warm-text-secondary hover:text-warm-text-primary transition-colors"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={onSignIn}
            disabled={isLoading}
            className="text-sm font-medium px-4 py-2 rounded-soft landing-gradient-bg text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Get Started
          </button>
        </nav>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-warm-text-secondary"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-warm-bg-card border-t border-warm-border px-6 py-4 space-y-3">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm text-warm-text-secondary hover:text-warm-text-primary py-2"
            >
              {link.label}
            </a>
          ))}
          <button
            onClick={onSignIn}
            disabled={isLoading}
            className="w-full text-sm font-medium px-4 py-2.5 rounded-soft landing-gradient-bg text-white"
          >
            Get Started
          </button>
        </div>
      )}
    </header>
  );
}
